import React, { useState } from 'react';
import { Message } from '../../store/services/whatsappApi';
import MessageReaction from './MessageReaction';
import MediaMessage from './MediaMessage';
import MessageActions from './MessageActions';
import MessageErrorDetails from './MessageErrorDetails';

interface MessageBubbleProps {
  message: Message;
  onDelete: (messageId: number) => void;
  renderTemplateContent?: (message: Message) => React.ReactNode;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onDelete,
  renderTemplateContent
}) => {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Pending';
      
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + 
               ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    } catch {
      return 'Pending';
    }
  };

  return (
    <div className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`max-w-xs md:max-w-md p-3 rounded-lg relative group ${
          message.direction === 'outbound' 
            ? 'bg-whatsapp-light-green text-gray-800 rounded-br-none' 
            : 'bg-white text-gray-800 rounded-bl-none'
        }`}
      >
        {/* Contact info */}
        {message.contact && (
          <div className="text-xs text-gray-500 mb-1 font-semibold">
            {message.contact.name} • {message.contact.phone_number}
          </div>
        )}
        
        {/* Media content */}
        {(message.message_type === 'media' || 
          message.message_type === 'image' || 
          message.message_type === 'video' || 
          message.message_type === 'audio' || 
          message.message_type === 'document') && 
          message.media_url && (
          <div className="mb-2">
            <MediaMessage 
              mediaUrl={message.media_url} 
              mediaType={message.message_type} 
              mediaMetadata={message.media_metadata}
              fileName={message.content && message.content.includes('whatsapp_media_') ? undefined : undefined}
              caption={message.content && !message.content.includes('whatsapp_media_') ? message.content : undefined}
            />
          </div>
        )}
        
        {/* Message content - only show for non-media messages */}
        {message.message_type === 'template' 
          ? (renderTemplateContent ? renderTemplateContent(message) : <p>{message.content}</p>)
          : (message.content && !message.content.includes('whatsapp_media_') && 
             !['media', 'image', 'video', 'audio', 'document'].includes(message.message_type) && 
             <p>{message.content}</p>)}
        
        {/* Message reactions */}
        {message.reactions && (
          <div className="mt-1 flex justify-end">
            <MessageReaction reaction={message.reactions} />
          </div>
        )}
        
        {/* Timestamp and status indicators */}
        <p className="text-xs text-gray-500 text-right mt-1">
          {message.timestamp && formatTimestamp(message.timestamp)}
          {message.direction === 'outbound' && (
            <span className="ml-2">
              {message.status === 'delivered' && '✓✓'}
              {message.status === 'read' && <span className="text-blue-500">✓✓</span>}
              {message.status === 'sent' && '✓'}
              {message.status === 'failed' && (
                <button
                  onClick={() => setShowErrorDetails(true)}
                  className="text-red-500 hover:text-red-700 cursor-pointer"
                  title="Click to view error details"
                >
                  ⚠️
                </button>
              )}
            </span>
          )}
        </p>
        
        {/* Message Actions Component */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <MessageActions
            message={message}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Error Details Modal */}
      <MessageErrorDetails
        message={message}
        isOpen={showErrorDetails}
        onClose={() => setShowErrorDetails(false)}
      />
    </div>
  );
};

export default MessageBubble;
